import { Resource } from '../classes/resource/resource.class';
import { Presentation } from '../classes/presentation/presentation.class';
import { Quarter } from '@omni/types/shared.type';
import { bufferTime, filter, mergeMap, Subject } from 'rxjs';
import { compact, join } from 'lodash-es';

export class Utility {

    private static globalCustomerReplaceText: string = "";
    private static globalCustomersReplaceText: string = "";


    /**
     * Returns true if the current time subtract the lastModified date is greater than the tolerance (EXPIRED)
     *
     * @static
     * @param {Date} lastModifiedDate
     * @param {number} tolerance
     * @returns {boolean}
     * @memberof Utility
     */
    public static isExpired(lastModifiedDate: number, tolerance: number): boolean {
        const currentTime = new Date().getTime();
        if (currentTime - lastModifiedDate >= tolerance) {
            return true;
        } else {
            return false;
        }
    }

    static changeUTCDateToLocalDateWith0Time(utcTimestamp: number,isEndDate:boolean = false) {
        const utc = new Date(utcTimestamp);
        const utcFullYear = utc.getUTCFullYear();
        const utcMonthIndex = utc.getUTCMonth();
        const utcDate = utc.getUTCDate();
        if(isEndDate){
          return new Date(utcFullYear, utcMonthIndex, utcDate, 23, 59, 59)
        }else{
          return new Date(utcFullYear, utcMonthIndex, utcDate, 0, 0, 0)
        }
    }

    static changeLocalDateToUTCDateWith0Time(localTimestamp: number,isEndDate:boolean = false) {
        var date = new Date(localTimestamp);
        var now_utc =  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(),
         0, 0, 0);
         return new Date(now_utc);
    }

    public static getFormattedNumber(value:number) :string{
        let str = "0.00"
        if(value){
            str =  value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return str;
    }

    public static delay(ms: number): Promise<any> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    public static getRoundedDownTime(dateTime:Date) {
        if(dateTime.getMinutes() < 30){
          dateTime.setMinutes(0);
          dateTime.setSeconds(0);
          dateTime.setMilliseconds(0);
          return dateTime;
        }else{
          dateTime.setMinutes(30);
          dateTime.setSeconds(0);
          dateTime.setMilliseconds(0);
          return dateTime;
        }
    }

    public static getRoundedUpTime(dateTime:Date) {
      if (dateTime.getMinutes() < 30) {
        dateTime.setMinutes(30);
        dateTime.setSeconds(0);
        dateTime.setMilliseconds(0);
        return dateTime;
      } else {
        dateTime.setMinutes(0);
        dateTime.setHours(dateTime.getHours() + 1);
        dateTime.setSeconds(0);
        dateTime.setMilliseconds(0);
        return dateTime;
      }
    }

    public static get globalCustomerText(): string {
      return this.globalCustomerReplaceText;
    }

    public static set globalCustomerText(text) {
      this.globalCustomerReplaceText = text;
    }

    public static get globalCustomersText(): string {
      return this.globalCustomersReplaceText;
    }

    public static set globalCustomersText(text) {
      this.globalCustomersReplaceText = text;
    }

    public static getUrlParams(search) {
      const hashes = search.slice(search.indexOf('?') + 1).split('&');
      const params = {};
      hashes.map(hash => {
        const [key, val] = hash.split('=')
        params[key] = decodeURIComponent(val)
      });
      return params
    }

    public static getCommaSeparatedString(array: string[]): string {
        let returnString = '';
        try {
            let tempArray = compact(array);

            if (tempArray.length > 0) {
                returnString = join(tempArray, ', ');
            }
        } catch (error) {
            console.error('getCommaSeparatedString: ', error);
        }

        return returnString.trim();
    }

    public static filterByDate(resource: Resource | Presentation) {
        let currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        if (resource.availableFrom && resource.availableFrom > currentDate) {
          return false;
        }
        if (resource.availableUntil && resource.availableUntil < currentDate) {
          return false;
        }
        return true;
      }

    public static getCurrentQuarter(): Quarter {
      const today = new Date();
      // Quarter derived from month index: Math.ceil((month + 1) / 3) gives 1–4
      const quarter = Math.ceil((today.getMonth() + 1) / 3);
      return !isNaN(quarter) && (quarter === 1 || quarter === 2 || quarter === 3 || quarter === 4) ? quarter : null;
    }
    public static getZeroBasedQuaterMonthIdxes(quarter: Quarter): number[] {
      let quarterMonths: number[];
      switch (quarter) {
        case 1:
          quarterMonths = [0, 1, 2];
          break;

        case 2:
          quarterMonths = [3, 4, 5];
          break;

        case 3:
          quarterMonths = [6, 7, 8];
          break;

        case 4:
          quarterMonths = [9, 10, 11];
          break;

        default:
          break;
      }

      return quarterMonths;
    }

      public static getDBBucketedKey(key, bucket_number){
        return `${key}_bucket_${bucket_number}`
      }
}

export const toBase64 = file => new Promise((resolve, reject) => {
  const reader = getFileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});

export function getFileReader(): FileReader {
  const fileReader = new FileReader();
  const zoneOriginalInstance = (fileReader as any)["__zone_symbol__originalInstance"];
  return zoneOriginalInstance || fileReader;
}

/**
 * Error thrown by {@link withTimeout} when the wrapped promise does not settle in time.
 */
export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Rejects with a {@link TimeoutError} if `promise` has not settled within `timeoutMs`.
 *
 * Use this to bound native-bridge calls that can hang indefinitely when the host
 * process is suspended — e.g. the ADAL silent-token renewal on the Electron/desktop
 * build, which never invokes its `cordova.exec` callback after a long OS sleep and
 * deadlocks the whole sync pipeline (OMNI-60515). We stop awaiting the original
 * promise; it is left to settle (or not) on its own.
 */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label = 'operation'): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export const MAXIMUM_NOTE_ATTACHMENT_SIZE = 5120;

/** Human-readable file size (e.g. "820 KB", "1.4 MB") from a byte count. */
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 1024) {
    return `${bytes || 0} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const NOTE_ATTACHMENT_MIME_TYPES_SUPPORTED_REGEX = new RegExp("[^\\s]+(.*?)\\.(jpg|jpeg|png|gif|JPG|JPEG|PNG|GIF|pdf|PDF|doc|DOC|docx|DOCX|txt|TXT|xls|XLS|xlsx|XLSX|csv|CSV|ppt|pptx|PPT|PPTX|rar|RAR|mp4|MP4|mp3|MP3)$");

export const NOTE_ATTACHMENT_MIME_TYPES_VIDEO_RESTRICTED_REGEX = new RegExp("[^\\s]+(.*?)\\.(mp4|MP4|mov|MOV|avi|AVI|mkv|MKV|wmv|WMV)$");

export const DCR_ATTACHMENT_MIME_TYPES_SUPPORTED_REGEX = new RegExp("[^\\s]+(.*?)\\.(jpg|png)$");

export const CONTRACT_SUPPORTED_MIME_TYPES_SUPPORTED_REGEX = new RegExp("[^\\s]+(.*?)\\.(PDF|pdf|jpg|png|DOC|docx|DOCX|txt|TXT|xls|XLS|xlsx|XLSX|ppt|pptx|PPT|PPTX|msg|odt)$");

export const shortMonthArray =  ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export enum PopoverMode {
    FOR_JOURNEY_INSIGHT_DESCRIPTION,
    FOR_JOURNEY_INSIGHT_COMPETITOR_PRODUCT,
    FOR_INTEREST_INSIGHT_DESCRIPTION
}

export enum CustomerSelectionMode {
    FOR_INSIGHT, FOR_INSIGHT_RELATIONSHIP
}

export enum OptionSelectionMode {
    SELECT_SPECIALITY, SELECT_INTEREST_CATEGORY, SELCET_JOURNEY_INSIGHT_PRODUCT
}

export enum CustomerReplaceText {
    GLOBAL_CUSTOMER_REPLACE_TEXT = 'CUSTOMER',
    GLOBAL_STAKEHOLDER_REPLACE_TEXT = 'STAKEHOLDER',
    GLOBAL_CUSTOMERS_REPLACE_TEXT = 'CUSTOMERS',
    GLOBAL_STAKEHOLDERS_REPLACE_TEXT = 'STAKEHOLDERS',
}

export const MAXIMUM_PHOTO_CAPTURE_LIMIT = 10;

export enum DynamicsConstant {
  pagingCookie = "@Microsoft.Dynamics.CRM.fetchxmlpagingcookie",
  hasMoreRecords = "@Microsoft.Dynamics.CRM.morerecords"
}
export class HTTPRequestTraceUtility {

  private dictNetworkProgress: { [key: string]: boolean } = {};

  private static instance: HTTPRequestTraceUtility;

  public static getInstance(): HTTPRequestTraceUtility {
    if (!HTTPRequestTraceUtility.instance) {
      HTTPRequestTraceUtility.instance = new HTTPRequestTraceUtility();
    }
    return HTTPRequestTraceUtility.instance;
  }

  private constructor(){
    this.dictNetworkProgress = {};
  }

  public isNetworkCallInProgress(key: string): boolean {
    return this.dictNetworkProgress[key];
  }

  public setNetworkCallInProgress(key: string, value: boolean): void {
    this.dictNetworkProgress[key] = value;
  }

  public isAtleastOneNetworkCallInProgress(): boolean {
    return Object.values(this.dictNetworkProgress).some(x => x);
  }
}


type InputWithId<T> = { id: string } & T;
type BatchResult<R> = { id: string; result: R };


export function batched<T extends object, R>(
  batchProcessor: (inputs: Array<InputWithId<T>>) => Promise<BatchResult<R>[]>,
  debounceMs = 10
) {
  const requestSubject = new Subject<{
    input: InputWithId<T>,
    resolve: (value: R | undefined) => void
  }>();

  requestSubject.pipe(
    bufferTime(debounceMs),
    filter(batch => batch.length > 0),
    mergeMap(async batch => {
      const inputs = batch.map(b => b.input);
      const resolvers = batch.reduce((acc, item) => {
        acc[item.input.id] = item.resolve;
        return acc;
      }, {} as Record<string, (value: R | undefined) => void>);

      try {
        const results = await batchProcessor(inputs);
        const resultMap = new Map(results.map(r => [r.id, r.result]));

        for (const id in resolvers) {
          resolvers[id](resultMap.get(id));
        }
      } catch (err) {
        for (const id in resolvers) {
          resolvers[id](undefined); // Or reject if needed
        }
      }
    })
  ).subscribe();

  return function batchRequest(input: InputWithId<T>): Promise<R | undefined> {
    return new Promise<R | undefined>((resolve) => {
      requestSubject.next({ input, resolve });
    });
  };
}
